import type { ManagerContext } from './manager.xstate.types';

/**
 * Resolve the display name of a just-deleted project for the success toast.
 *
 * The homepage renders `projectsNew` (Hasura-sourced, matched by `id`), while
 * the legacy `projects` array (admin_ GraphQL, matched by `projectId`) is not
 * always populated on that screen. Looking the name up only in `projects`
 * produced the bug "Project undefined deleted successfully". Prefer
 * `projectsNew`, then fall back to the legacy `projects`.
 *
 * The only Delete Project button lives on the project-details page, which
 * populates neither list — it loads `projectDetails` alone. So on a deep link,
 * bookmark, or plain F5 into that page (without first visiting Homepage or All
 * Projects), both arrays are still empty and the lookups miss. `projectDetails`
 * is guaranteed loaded there and is the delete target itself, so use its name
 * (matched by `projectId`) as a final fallback to keep the toast correct.
 */
export function resolveDeletedProjectName(
  projectId: string,
  sources: {
    projectsNew: ManagerContext['projectsNew'];
    projects: ManagerContext['projects'];
    projectDetails?: ManagerContext['projectDetails'];
  }
): string | undefined {
  const { projectsNew, projects, projectDetails } = sources;

  const fromNew = projectsNew?.find(
    (project) => project?.id === projectId
  )?.name;
  const fromLegacy = projects?.find(
    (project) => project?.projectId === projectId
  )?.name;
  const fromDetails =
    projectDetails?.projectId === projectId ? projectDetails?.name : undefined;

  return fromNew ?? fromLegacy ?? fromDetails ?? undefined;
}
